import * as React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import VariantSelector from "Catalog/Preview/Page/VariantSelector"
import CatalogSelector from "Catalog/shared/components/CatalogSelector"
import CatalogToolbar from "Catalog/shared/components/CatalogToolbar"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import usePageHeaderLinks from "Catalog/shared/hooks/usePageHeaderLinks"
import Layout from "shared/components/Layout"
import useAutoscroll from "shared/hooks/useAutoscroll"
import useEditor from "shared/hooks/useEditor"
import { CategoryId, DealId, ProductId } from "shared/hubrise/ids"
import { ICategory, IDeal, IProduct, isDeal, isProduct } from "shared/hubrise/models"
import { Category, Product } from "shared/hubrise/utils"
import * as Deal from "shared/hubrise/utils/Deal"
import { fakeDealCategory } from "shared/hubrise/utils/Deal"

import DealPopup from "../Popup/DealPopup"
import ProductPopup from "../Popup/ProductPopup"
import ProductList from "../ProductList"

import { StyledPage, EmptyCatalog } from "./Styles"

const Page = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ productId: string; categoryId: string; dealId: string }>()

  const { catalog } = useEditor()

  const { catalogNavigation } = useCatalogNavigation()
  const indexPath = catalogNavigation.previewPath("all")

  const $contentDivRef = React.useRef<HTMLDivElement | null>(null)
  const [popupContainer, setPopupContainer] = React.useState<HTMLDivElement | undefined>(undefined)

  const selectedItem = useFindItemOrRedirect<IProduct | IDeal | ICategory>(
    params.productId ?? params.dealId ?? params.categoryId,
    (id) => {
      if (params.productId !== undefined) {
        return Product.byId(catalog, ProductId(Number(id)))
      } else if (params.dealId !== undefined) {
        return Deal.byId(catalog, DealId(Number(id)))
      } else if (params.categoryId !== undefined) {
        return Category.byId(catalog, CategoryId(Number(id)))
      }
    },
    indexPath,
  )

  const [showPopup, setShowPopup] = React.useState(false)
  React.useEffect(() => {
    if (!selectedItem) setShowPopup(false)
  }, [selectedItem])

  let popup: JSX.Element | undefined
  if (showPopup && selectedItem !== undefined) {
    if (isProduct(selectedItem)) {
      popup = <ProductPopup product={selectedItem} rootElement={popupContainer} />
    } else if (isDeal(selectedItem)) {
      popup = <DealPopup deal={selectedItem} rootElement={popupContainer} />
    }
  }

  useAutoscroll(
    $contentDivRef,
    selectedItem ? String(selectedItem.id) : undefined,
    // We want to align the category with the top of the page when we click on it
    params.categoryId !== undefined ? { force: true, position: 0 } : {},
    () => setShowPopup(true), // Delay showing the popup until the scroll completes
  )

  const rootCategories = (() => {
    const topCategories = Category.topLevel(catalog)
    const category = fakeDealCategory(catalog, t("preview.deal_category_name"))
    return category ? [...topCategories, category] : topCategories
  })()

  const commandActions = <>{catalog.data.variants.length > 0 && <VariantSelector />}</>

  const links = usePageHeaderLinks("preview")
  const toolbar = <CatalogToolbar commandActions={commandActions} />

  return (
    <Layout
      catalogSelector={<CatalogSelector displayMode="preview" />}
      links={links}
      toolbar={toolbar}
      hasPopupOverlay={popup !== undefined}
      ref={(element) => {
        $contentDivRef.current = element
        if (element !== null) setPopupContainer(element)
      }}
    >
      <StyledPage>
        {rootCategories.length === 0 ? (
          <EmptyCatalog>{t("product.none")}</EmptyCatalog>
        ) : (
          <>
            <ProductList rootCategories={rootCategories} />
            {popup}
          </>
        )}
      </StyledPage>
    </Layout>
  )
}

export default Page
